import { GAME_GROUP_PARAM, NOT_MEMBER_REASON } from '@/utils/gameGroups';
import type { ProjectGroupDTO, ProjectGroupsEntryDTO } from '@/services/api.service';
import type { WorldsState } from './worlds.types';

// ============================================================
// Mondes et groupes de jeu (NationsGlory : Nova, Iris) : fonctions pures, sans store ni I/O
// ============================================================
// Carte commune : Nova (l'éditeur) est le seul à l'écrire ; le satellite d'Iris la suit (mode
// « follower » de /state) et pousse le roster vers la NGCountryAPI d'Iris. La page passe alors en
// lecture seule, sauf la synchro du roster, qui reste celle du groupe affiché.
// ============================================================

/** Le satellite du groupe affiché suit la carte commune d'un autre groupe. */
export function isFollower(state: WorldsState | null | undefined): boolean {
  return state?.mode === 'follower';
}

/** Groupe éditeur de la carte, avec le libellé et la couleur du CICD quand ils sont connus. */
export interface LeaderGroup {
  key: string;
  label: string;
  color?: string;
  /** Choisissable dans le sélecteur de groupe (bouton « Passer sur Nova »). */
  available: boolean;
}

/**
 * Éditeur de la carte commune : `leader` annoncé par le satellite suiveur, sinon la racine du projet
 * (le satellite d'entrée, Nova). Sans groupes chargés, la clé sert de libellé (« nova » → « Nova »).
 */
export function leaderGroup(state: WorldsState | null | undefined, entry: ProjectGroupsEntryDTO | undefined): LeaderGroup | null {
  const key = (state?.leader ?? entry?.root ?? '').trim().toLowerCase();
  if (!key) return null;
  const g = entry?.groups.find((x) => x.key === key);
  return g
    ? { key, label: g.label, color: g.color, available: g.available }
    : { key, label: key.charAt(0).toUpperCase() + key.slice(1), available: false };
}

/**
 * Mondes n'est pas activé sur le groupe affiché (Iris sans module worlds) : son entrée, pour dire
 * « Mondes n'est pas activé sur Iris » au lieu du 404 brut du module. null si on ne sait pas
 * (capacités jamais sondées) ou si le module est annoncé.
 */
export function worldsMissingOn(entry: ProjectGroupsEntryDTO | undefined, groupKey: string | null | undefined): ProjectGroupDTO | null {
  if (!entry || !groupKey) return null;
  const g = entry.groups.find((x) => x.key === groupKey);
  if (!g || g.capabilities.length === 0) return null;
  return g.capabilities.includes('worlds') ? null : g;
}

/**
 * Éditeur de la carte commune : les autres groupes disponibles qui annoncent le module worlds, donc
 * qui suivent cette carte (décision W2 : un seul éditeur). Vide pour un jeu sans groupes. Un groupe
 * hors de l'affectation du membre du staff (`not-member`) compte aussi : il suit la carte éditée ici.
 */
export function followersOf(entry: ProjectGroupsEntryDTO | undefined, groupKey: string | null | undefined): ProjectGroupDTO[] {
  if (!entry || !groupKey) return [];
  return entry.groups.filter((g) => g.key !== groupKey && (g.available || g.reason === NOT_MEMBER_REASON)
    && g.capabilities.includes('worlds'));
}

/**
 * Paramètres d'une navigation interne à la page (onglet, pays...) qui gardent le groupe de l'URL
 * (`?g=`) : un lien copié depuis la barre d'adresse rouvre le même groupe (correction L10).
 */
export function withGameGroup(next: URLSearchParams | Record<string, string>, current: URLSearchParams): URLSearchParams {
  const out = new URLSearchParams(next);
  const g = current.get(GAME_GROUP_PARAM);
  if (g && !out.has(GAME_GROUP_PARAM)) out.set(GAME_GROUP_PARAM, g);
  return out;
}
